/**
 * @fileoverview Client side of the app version gate. Calls
 * GET /api/app/version-check on launch and whenever the app returns to the
 * foreground, and exposes the result so the root can show a blocking
 * "update required" screen or a dismissible "update recommended" nudge.
 *
 * Fails OPEN: if the check errors (offline, server hiccup) we never block —
 * `updateRequired` stays false so the app keeps working.
 */

import { useState, useEffect, useCallback } from 'react';
import { AppState, Platform } from 'react-native';

import { api } from './client';

export interface VersionCheck {
  platform: 'ios' | 'android' | null;
  current: string | null;
  minVersion: string;
  latestVersion: string;
  updateRequired: boolean;
  updateRecommended: boolean;
  storeUrl: string | null;
  message: string | null;
}

/** One-shot fetch of the version-check result (null on failure / web). */
export async function fetchVersionCheck(): Promise<VersionCheck | null> {
  if (Platform.OS === 'web') return null;
  try {
    return await api<VersionCheck>('/api/app/version-check', { skipAuth: true });
  } catch {
    return null; // fail open
  }
}

/**
 * Hook: re-checks on mount and on every foreground resume so a client kept
 * open across a min-version bump still gets gated next time it's focused.
 */
export function useVersionGate(): VersionCheck | null {
  const [info, setInfo] = useState<VersionCheck | null>(null);

  const check = useCallback(async () => {
    const r = await fetchVersionCheck();
    if (r) setInfo(r);
  }, []);

  useEffect(() => { void check(); }, [check]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', s => {
      if (s === 'active') void check();
    });
    return () => sub.remove();
  }, [check]);

  return info;
}
